import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Activity, Package, FileText, Truck, RefreshCw, Trash2 } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { adminAPI } from '@/services/api';
import { toast } from '@/components/ui/use-toast';
import { useConstantsContext } from '@/contexts/ConstantsContext';
import OperationsRequestsList from '@/components/admin/operations/OperationsRequestsList';
import OperationsOffersList from '@/components/admin/operations/OperationsOffersList';
import OperationsBookingsList from '@/components/admin/operations/OperationsBookingsList';
import OperationsDeletedList from '@/components/admin/operations/OperationsDeletedList';

const AdminOperations = () => {
  // Utiliser les constantes du contexte partagé
  const { constants } = useConstantsContext();

  const [activeTab, setActiveTab] = useState('requests');
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({
    requests: {
      total: 0,
      pending: 0,
      assigned: 0,
      in_transit: 0,
      completed: 0,
      cancelled: 0,
    },
    offers: {
      total: 0,
      pending: 0,
      accepted: 0,
      rejected: 0,
    },
    bookings: {
      total: 0,
      confirmed: 0,
      in_progress: 0,
      delivered: 0,
      completed: 0,
      cancelled: 0,
    },
  });

  // Ne charger les statistiques qu'une seule fois au montage
  const hasLoadedRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    if (!hasLoadedRef.current && isMountedRef.current) {
      hasLoadedRef.current = true;
      loadStatistics();
    }

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      // Charger les statistiques pour chaque section
      const [requestsRes, offersRes, bookingsRes] = await Promise.all([
        adminAPI.getAllRequests({}),
        adminAPI.getAllOffers({}),
        adminAPI.getAllBookings({}),
      ]);

      const requests = requestsRes.data.results || requestsRes.data || [];
      const offers = offersRes.data.results || offersRes.data || [];
      const bookings = bookingsRes.data.results || bookingsRes.data || [];

      setStatistics({
        requests: {
          total: requests.length,
          pending: requests.filter(r => r.status === 'PUBLISHED' || r.status === 'DRAFT').length,
          assigned: requests.filter(r => r.status === 'ASSIGNED').length,
          in_transit: requests.filter(r => r.status === 'IN_TRANSIT').length,
          completed: requests.filter(r => r.status === 'COMPLETED').length,
          cancelled: requests.filter(r => r.status === 'CANCELLED').length,
        },
        offers: {
          total: offers.length,
          pending: offers.filter(o => o.status === 'PENDING').length,
          accepted: offers.filter(o => o.status === 'ACCEPTED').length,
          rejected: offers.filter(o => o.status === 'REJECTED').length,
        },
        bookings: {
          total: bookings.length,
          confirmed: bookings.filter(b => b.status_code === 'CONFIRMED').length,
          in_progress: bookings.filter(b => b.status_code === 'IN_PROGRESS').length,
          delivered: bookings.filter(b => b.status_code === 'DELIVERED').length,
          completed: bookings.filter(b => b.status_code === 'COMPLETED').length,
          cancelled: bookings.filter(b => b.status_code === 'CANCELLED').length,
        },
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les statistiques',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Callbacks pour mettre à jour les statistiques (définis au niveau supérieur)
  const handleRequestsStatsUpdate = useCallback((stats) => {
    setStatistics(prev => ({
      ...prev,
      requests: stats,
    }));
  }, []);

  const handleOffersStatsUpdate = useCallback((stats) => {
    setStatistics(prev => ({
      ...prev,
      offers: stats,
    }));
  }, []);

  const handleBookingsStatsUpdate = useCallback((stats) => {
    setStatistics(prev => ({
      ...prev,
      bookings: stats,
    }));
  }, []);

  return (
    <>
      <Helmet>
        <title>Gestion des Opérations - LogiConnect Admin</title>
      </Helmet>
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                Gestion des Opérations
              </h1>
              <p className="text-muted-foreground mt-1">
                Vue d'ensemble et gestion centralisée de toutes les opérations de transport
              </p>
            </div>
            <Button
              variant="outline"
              onClick={loadStatistics}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>

          {/* Statistiques globales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Demandes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{statistics.requests.total}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {statistics.requests.pending} en attente • {statistics.requests.in_transit} en transit
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Offres
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{statistics.offers.total}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {statistics.offers.pending} en attente • {statistics.offers.accepted} acceptées
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Missions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{statistics.bookings.total}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {statistics.bookings.in_progress} en cours • {statistics.bookings.completed} terminées
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Onglets principaux */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="requests" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Demandes
                {statistics.requests.pending > 0 && (
                  <span className="ml-1 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                    {statistics.requests.pending}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="offers" className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Offres
                {statistics.offers.pending > 0 && (
                  <span className="ml-1 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                    {statistics.offers.pending}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="bookings" className="flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Missions
                {statistics.bookings.in_progress > 0 && (
                  <span className="ml-1 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                    {statistics.bookings.in_progress}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="deleted" className="flex items-center gap-2">
                <Trash2 className="w-4 h-4" />
                Supprimés
              </TabsTrigger>
            </TabsList>

            <TabsContent value="requests" className="mt-6">
              {activeTab === 'requests' && (
                <OperationsRequestsList
                  constants={constants}
                  onStatisticsUpdate={handleRequestsStatsUpdate}
                />
              )}
            </TabsContent>

            <TabsContent value="offers" className="mt-6">
              {activeTab === 'offers' && (
                <OperationsOffersList
                  constants={constants}
                  onStatisticsUpdate={handleOffersStatsUpdate}
                />
              )}
            </TabsContent>

            <TabsContent value="bookings" className="mt-6">
              {activeTab === 'bookings' && (
                <OperationsBookingsList
                  constants={constants}
                  onStatisticsUpdate={handleBookingsStatsUpdate}
                />
              )}
            </TabsContent>

            <TabsContent value="deleted" className="mt-6">
              {activeTab === 'deleted' && <OperationsDeletedList />}
            </TabsContent>
          </Tabs>
        </div>
      </DashboardLayout>
    </>
  );
};

export default AdminOperations;
